import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';

import getDataByCountryCode from 'src/utils/countryData';
import useMerchant from './useMerchant';

//
//

export const useDate = () => {
  const { merchant } = useMerchant();

  const [locale, setLocale] = useState<any>(null);

  const merchantLocale = useMemo(() => {
    return getDataByCountryCode(merchant?.country)?.locale;
  }, [merchant?.country]);

  const getDateFnsLocale = useCallback(async (localeCode: string) => {
    if (!merchantLocale) return;
    try {
      // 1st attempt: Try to load the exact locale (e.g., 'en-US')
      try {
        const module = await import(`date-fns/locale/${localeCode}/index.js`);
        return module.default;
      } catch (exactError) {
        // 2nd attempt: Try to load using camelCase (e.g., 'enUS')
        try {
          const camelCaseLocale = localeCode.replace(/-([a-z])/gi, (_: any, letter: string) =>
            letter.toUpperCase()
          );
          const module = await import(`date-fns/locale/${camelCaseLocale}/index.js`);
          return module.default;
        } catch (camelError) {
          // 3rd attempt: Try with language code only (e.g., 'en')
          const languageCode = localeCode.split('-')[0];
          const module = await import(`date-fns/locale/${languageCode}/index.js`);
          return module.default;
        }
      }
    } catch (error) {
      console.error(`Failed to load locale for "${localeCode}":`, error);
      // Fallback to en-NZ locale
      const enModule = await import('date-fns/locale/en-NZ/index.js');
      return enModule.default;
    }
  }, [merchantLocale]);

  useEffect(() => {
    const loadLocale = async () => {
      const localeModule = await getDateFnsLocale(merchantLocale as string);
      setLocale(localeModule);
    };

    loadLocale();
  }, [getDateFnsLocale, merchantLocale]);

  const getLocalizedDateSync = useCallback(
    (date: Date | number | string, formatString: string = 'P'): string => {
      try {
        const dateObj = date instanceof Date ? date : new Date(date);
        getDateFnsLocale(merchantLocale as string);

        return format(dateObj, formatString, { locale });
      } catch (error) {
        console.error('Error formatting date:', error);
        return String(date);
      }
    },
    [getDateFnsLocale, locale, merchantLocale]
  );

  //

  return {
    getLocalizedDateSync,
    locale,
    merchantLocale,
  };
};
